import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { IUser } from 'lavva.exalushome/build/js/Services/Users/IUser';
import { IconEye } from '../../../../assets/icons/icon-eye';
import {
  CopyToClipboardButton,
  CustomBackdrop,
  DialogConfirmation,
  Header,
  NavHeader,
  Page,
} from '../../../../components';
import ArrowButton from '../../../../components/arrow-button';
import { ROUTES } from '../../../../routes/routes';
import { ControllerQrCode } from '../../components/controller-qr-code';
import { PopupType } from '../../enums/controller';
import { useLoggedUser } from '../../hooks/common/logged-user';
import { useExalusUpdatesSettingsAvailable } from '../../hooks/updates';
import { useExalusUserImg } from '../../hooks/users';
import { ExalusIntegrationForm } from '../integration-create/types';
import ExalusUserMainInfo from '../users-list/user-details/user-data/main-info';
import { ControllerVersionButton } from './components/controller-version-button/controller-version-button';
import { useControllerSettings } from './hooks/use-controller-settings';
import { useControllerUpdates } from './hooks/use-controller-updates';
import { useImportExport } from './hooks/use-import-export';
import './index.scss';

type ExalusControllerSettingsProps = {
  noHeader?: boolean;
};

const ExalusControllerSettings: React.FC<ExalusControllerSettingsProps> = ({ noHeader }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('installation');
  const { t: tc } = useTranslation('common');
  const [showPin, setShowPin] = useState<boolean>(false);
  const [exalusQrCode, setExalusQrCode] = useState<ExalusIntegrationForm>({ serialNumber: '', pin: '' });
  const { popup, popupType, controllerInfo, loading, setPopup, showDialog, handleConfirm, fastSyncEnabled } =
    useControllerSettings();
  const { getCurrentControllerVersion, versionPopup, setVersionPopup, controllerVersion } = useControllerUpdates();
  const { importExportEnabled } = useImportExport();
  const { user: userExalus, hasAccessToControllerSettings } = useLoggedUser();
  const { data: imageExalus, isLoading: imageExalusLoading } = useExalusUserImg(userExalus as IUser);
  const { data: updatesSettingsAvailable } = useExalusUpdatesSettingsAvailable();

  const additionalControllerButtonsVisible = useMemo(() => {
    if (userExalus?.AccessLevel) return userExalus.AccessLevel >= 30;
    return false;
  }, [userExalus]);

  const togglePin = () => setShowPin(!showPin);

  const handleShare = () => {
    setExalusQrCode({
      serialNumber: controllerInfo?.serialNumber || '',
      pin: controllerInfo?.pin || '',
    });
  };

  return (
    <Page
      isStickyHeader={!noHeader}
      className={classNames('controller-settings', { 'no-padding': noHeader })}
      {...(!noHeader
        ? {
            header: (
              <>
                <NavHeader />
                <Header title={t('exalus.settingsController')} isUnderline />
              </>
            ),
          }
        : {})}
    >
      <ExalusUserMainInfo user={userExalus} image={imageExalus} />
      <div className="controller-info">
        <div className="readonly-info">
          <p className="label">
            {t('exalus.serialNumber')}
            {controllerInfo?.serialNumber && <CopyToClipboardButton text={controllerInfo.serialNumber} />}
          </p>
          <p className="value">{controllerInfo?.serialNumber || '-'}</p>
        </div>
        <div className="readonly-info">
          <p className="label">
            {t('exalus.pin')}
            {controllerInfo?.pin && <CopyToClipboardButton text={controllerInfo.pin} />}
          </p>
          {showPin ? (
            <p className="value clickable" onClick={togglePin}>
              {controllerInfo?.pin || '-'}
            </p>
          ) : (
            <button onClick={togglePin} className="show_pin">
              <IconEye />
            </button>
          )}
        </div>
      </div>

      {hasAccessToControllerSettings && (
        <>
          <div className="grid-list-24">
            <ArrowButton title={t('share')} onClick={handleShare} disabled={!controllerInfo} className="m-b-24" />
          </div>
          <hr />

          <div className="grid-list-24 m-t-24">
            <ArrowButton
              title={t('exalus.location')}
              onClick={() => navigate(ROUTES.ExalusControllerLocation())}
              className="m-b-24"
            />
            <ArrowButton
              title={t('networkSettings')}
              onClick={() => navigate(ROUTES.ExalusNetworkSettings())}
              className="m-b-24"
            />
            <ArrowButton
              title={t('exalusUsers')}
              onClick={() => navigate(ROUTES.ExalusUsersList())}
              className="m-b-24"
            />
            <ArrowButton
              title={t('softUpdate')}
              onClick={() => navigate(ROUTES.ExalusControllerUpdates())}
              className="m-b-24"
            />
            {updatesSettingsAvailable && (
              <ArrowButton
                title={t('updatesSettings')}
                onClick={() => navigate(ROUTES.ExalusControllerUpdatesSettings())}
                className="m-b-24"
              />
            )}
            <ControllerVersionButton
              show={versionPopup}
              setShow={setVersionPopup}
              controllerVersion={controllerVersion}
              getCurrentControllerVersion={getCurrentControllerVersion}
            />
            <ArrowButton
              title={t('exalus.syncStatesMethod')}
              onClick={() => showDialog(PopupType.StateSync)}
              className="m-b-24"
            />
            {importExportEnabled && (
              <ArrowButton
                title={t('exalus.importExport')}
                onClick={() => navigate(ROUTES.ExalusControllerImportExport())}
                className="m-b-24"
              />
            )}
            {additionalControllerButtonsVisible && (
              <>
                <ArrowButton
                  title={t('exalus.restartController')}
                  onClick={() => showDialog(PopupType.Restart)}
                  className="m-b-24"
                />
                <ArrowButton title={t('exalus.resetController')} onClick={() => showDialog(PopupType.Reset)} />
              </>
            )}
          </div>
        </>
      )}

      <CustomBackdrop loading={imageExalusLoading} />
      <DialogConfirmation
        show={popup}
        setShow={setPopup}
        header={t(`exalus.${popupType?.toLowerCase()}DialogTitle`)}
        content={t(
          `exalus.${popupType?.toLowerCase()}${
            popupType === PopupType.StateSync ? (fastSyncEnabled ? 'Off' : 'On') : ''
          }DialogContent`,
        )}
        primaryBtnText={!loading ? t(`exalus.${popupType?.toLowerCase()}Controller`) : tc('buttons.loading')}
        secondaryBtnText={tc('buttons.cancel')}
        secondaryBtnAction={() => setPopup(false)}
        primaryBtnAction={handleConfirm}
        direction="column"
        primaryBtnDisabled={loading}
        timer={popupType === PopupType.Reset || popupType === PopupType.Restart}
        {...(popupType === PopupType.Reset &&
          controllerInfo && { confirmInput: { label: t('exalus.pin'), value: controllerInfo.pin } })}
      />
      <ControllerQrCode exalusQrCode={exalusQrCode} setShow={setExalusQrCode} />
    </Page>
  );
};

export default ExalusControllerSettings;
